import React, { forwardRef, HTMLAttributes, CSSProperties } from 'react';
import { Box, Typography } from '@mui/material';
import { Icon, theme, Button } from 'bs-unified-ui';
import { Icon as ChIcon } from '~/components/common/Icon';
import { Link } from 'react-router-dom';
import { DraggableSyntheticListeners } from '@dnd-kit/core';

export interface ShortContentCardProps extends HTMLAttributes<HTMLDivElement> {
  id: string;
  image: string;
  title: string;
  body: string;
  buttonLabel: string;
  buttonLink: string;
  onEdit: () => void;
  onDelete: () => void;
  withOpacity?: boolean;
  isDragging?: boolean;
  dragHandleRef?: (element: HTMLElement | null) => void;
  dragHandleListeners?: DraggableSyntheticListeners;
}

const ShortContentCard = forwardRef<HTMLDivElement, ShortContentCardProps>(
  (
    {
      id,
      image,
      title,
      body,
      buttonLabel,
      buttonLink,
      onEdit,
      onDelete,
      withOpacity,
      isDragging,
      dragHandleRef,
      dragHandleListeners,
      style,
      ...props
    },
    ref,
  ) => {
    const inlineStyles: CSSProperties = {
      opacity: withOpacity ? '0.5' : '1',
      ...style,
    };

    return (
      <div ref={ref} style={inlineStyles} {...props}>
        <Box
          sx={{
            'display': 'flex',
            'flexDirection': 'column',
            'borderRadius': '4px',
            'overflow': 'hidden',
            'position': 'relative',
            'height': '100%',
            '& a': {
              textDecoration: 'none !important',
            },
            '&:hover .action-buttons': {
              opacity: 1,
            },
          }}
        >
          <Box
            sx={{
              width: '100%',
              height: '192px',
            }}
          >
            <img
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
              }}
              src={image}
              alt=''
            />
          </Box>
          <Box
            sx={{
              backgroundColor: '#EDF0F7',
              p: 2,
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
              flex: 1,
            }}
          >
            <Typography variant='fs14' fontWeight={700}>
              {title}
            </Typography>
            <Typography variant='fs14'>{body}</Typography>
            <Link
              to={buttonLink}
              target='_blank'
              style={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                gap: '4px',
                padding: '4px 8px',
              }}
            >
              <Typography variant='fs14' fontWeight='400'>
                <Icon name='external-link' size={16} />
              </Typography>
              <Typography variant='fs14' fontWeight='600'>
                {buttonLabel}
              </Typography>
            </Link>
          </Box>

          {/* Action buttons overlay */}
          <Box
            className='action-buttons'
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              borderRadius: '4px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1,
              opacity: isDragging ? 1 : 0,
              transition: 'opacity 0.3s ease-in-out',
            }}
          >
            {isDragging ? (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1,
                  cursor: 'grabbing',
                }}
              >
                <ChIcon name='drag-move' size={24} color='white' />
                <Typography
                  variant='fs14'
                  fontWeight={700}
                  sx={{
                    color: 'white',
                    textAlign: 'center',
                  }}
                >
                  Drag and drop
                  <br />
                  to move the card
                </Typography>
              </Box>
            ) : (
              <>
                <Button
                  variant='contained'
                  onClick={(e) => {
                    e.stopPropagation();
                    onEdit();
                  }}
                  sx={{
                    'width': '97px',
                    'backgroundColor': 'white',
                    'color': `${theme.foreground.black} !important`,
                    'fontWeight': 600,
                    'gap': 0.5,
                    '&:hover': {
                      backgroundColor: 'white !important',
                    },
                  }}
                >
                  <ChIcon name='pencil' size={16} useBackgroundImg />
                  Edit
                </Button>
                <Button
                  variant='contained'
                  ref={dragHandleRef}
                  {...dragHandleListeners}
                  sx={{
                    'width': '97px',
                    'backgroundColor': 'white',
                    'color': `${theme.foreground.black} !important`,
                    'fontWeight': 600,
                    'gap': 0.5,
                    'cursor': 'grab',
                    '&:hover': {
                      backgroundColor: 'white !important',
                    },
                  }}
                >
                  <ChIcon name='drag-move' size={16} useBackgroundImg />
                  Move
                </Button>
                <Button
                  variant='contained'
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete();
                  }}
                  sx={{
                    'width': '97px',
                    'backgroundColor': 'white',
                    'color': `${theme.foreground.black} !important`,
                    'fontWeight': 600,
                    'gap': 0.5,
                    '&:hover': {
                      backgroundColor: 'white !important',
                    },
                  }}
                >
                  <ChIcon name='delete-gray' size={16} useBackgroundImg />
                  Delete
                </Button>
              </>
            )}
          </Box>
        </Box>
      </div>
    );
  },
);

ShortContentCard.displayName = 'ShortContentCard';

export default ShortContentCard;
